import { useState, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  Image,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useTeamScores, useIndividualScores } from "@/modules/scores";

type TabType = "teams" | "individuals";

/**
 * Main Scoreboard Screen
 * Shows team rankings and individual leaderboard
 */
export default function ScoreboardScreen() {
  const router = useRouter();
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const [activeTab, setActiveTab] = useState<TabType>("teams");
  const [refreshing, setRefreshing] = useState(false);

  const {
    teams,
    loading: teamsLoading,
    refresh: refreshTeams,
  } = useTeamScores();

  const {
    individuals,
    loading: individualsLoading,
    refresh: refreshIndividuals,
  } = useIndividualScores();

  /**
   * Handle pull-to-refresh
   */
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await Promise.all([refreshTeams(), refreshIndividuals()]);
    setRefreshing(false);
  }, [refreshTeams, refreshIndividuals]);

  /**
   * Get medal color for top 3 ranks
   */
  const getMedalColor = (rank: number): string | null => {
    switch (rank) {
      case 1:
        return "#FFD700"; // Gold
      case 2:
        return "#C0C0C0"; // Silver
      case 3:
        return "#CD7F32"; // Bronze
      default:
        return null;
    }
  };

  /**
   * Get initials from name
   */
  const getInitials = (name: string | null): string => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const loading = activeTab === "teams" ? teamsLoading : individualsLoading;

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      {/* Tab Switcher */}
      <View className="flex-row px-4 py-3">
        <TouchableOpacity
          className="flex-1 py-3 rounded-xl mr-2 items-center"
          style={{
            backgroundColor:
              activeTab === "teams" ? colors.primary : colors.surfaceSecondary,
          }}
          activeOpacity={0.7}
          onPress={() => setActiveTab("teams")}
          accessibilityRole="tab"
          accessibilityState={{ selected: activeTab === "teams" }}
        >
          <Text
            className="font-semibold"
            style={{
              color: activeTab === "teams" ? "#FFFFFF" : colors.textSecondary,
            }}
          >
            Teams
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          className="flex-1 py-3 rounded-xl ml-2 items-center"
          style={{
            backgroundColor:
              activeTab === "individuals"
                ? colors.primary
                : colors.surfaceSecondary,
          }}
          activeOpacity={0.7}
          onPress={() => setActiveTab("individuals")}
          accessibilityRole="tab"
          accessibilityState={{ selected: activeTab === "individuals" }}
        >
          <Text
            className="font-semibold"
            style={{
              color:
                activeTab === "individuals" ? "#FFFFFF" : colors.textSecondary,
            }}
          >
            Individu
          </Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        className="flex-1 px-4"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
            colors={[colors.primary]}
          />
        }
      >
        {/* Teams Tab */}
        {activeTab === "teams" && (
          <View className="pb-8">
            {loading ? (
              <View className="py-12 items-center">
                <Text style={{ color: colors.textSecondary }}>Laden...</Text>
              </View>
            ) : teams.length === 0 ? (
              <View className="py-12 items-center">
                <Ionicons
                  name="trophy-outline"
                  size={48}
                  color={colors.border}
                />
                <Text
                  className="mt-4 text-center"
                  style={{ color: colors.textSecondary }}
                >
                  Nog geen teams met scores
                </Text>
              </View>
            ) : (
              teams.map((team, index) => {
                const medalColor = getMedalColor(team.rank);
                return (
                  <TouchableOpacity
                    key={team.id}
                    className="rounded-xl p-4 mb-3 flex-row items-center"
                    style={{
                      backgroundColor: colors.surface,
                      borderWidth: medalColor ? 2 : 1,
                      borderColor: medalColor || colors.border,
                    }}
                    activeOpacity={0.7}
                    onPress={() => router.push(`/scores/team/${team.id}`)}
                    accessibilityRole="button"
                    accessibilityLabel={`${team.name}, ${team.total_score} punten, rang ${team.rank}`}
                  >
                    {/* Rank */}
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center mr-3"
                      style={{
                        backgroundColor: medalColor
                          ? `${medalColor}20`
                          : colors.surfaceSecondary,
                      }}
                    >
                      {medalColor ? (
                        <Ionicons
                          name="trophy"
                          size={20}
                          color={medalColor}
                        />
                      ) : (
                        <Text
                          className="font-bold text-lg"
                          style={{ color: colors.textTertiary }}
                        >
                          {team.rank}
                        </Text>
                      )}
                    </View>

                    {/* Team Avatar/Color */}
                    <View
                      className="w-12 h-12 rounded-xl items-center justify-center mr-3 overflow-hidden"
                      style={{
                        backgroundColor: team.color || colors.primaryLight,
                      }}
                    >
                      {team.avatar_url ? (
                        <Image
                          source={{ uri: team.avatar_url }}
                          className="w-full h-full"
                          resizeMode="cover"
                        />
                      ) : (
                        <Text
                          className="text-white font-bold text-lg"
                          style={{
                            color: team.color ? "#FFFFFF" : colors.primary,
                          }}
                        >
                          {team.name.charAt(0)}
                        </Text>
                      )}
                    </View>

                    {/* Team Info */}
                    <View className="flex-1">
                      <Text
                        className="font-semibold text-base"
                        style={{ color: colors.text }}
                      >
                        {team.name}
                      </Text>
                      <Text
                        className="text-sm"
                        style={{ color: colors.textSecondary }}
                      >
                        {team.member_count}{" "}
                        {team.member_count === 1 ? "lid" : "leden"}
                      </Text>
                    </View>

                    {/* Score */}
                    <View className="items-end">
                      <Text
                        className="font-bold text-xl"
                        style={{ color: colors.primary }}
                      >
                        {team.total_score.toLocaleString()}
                      </Text>
                      <Text
                        className="text-xs"
                        style={{ color: colors.textTertiary }}
                      >
                        punten
                      </Text>
                    </View>

                    <Ionicons
                      name="chevron-forward"
                      size={20}
                      color={colors.textTertiary}
                      style={{ marginLeft: 8 }}
                    />
                  </TouchableOpacity>
                );
              })
            )}
          </View>
        )}

        {/* Individuals Tab */}
        {activeTab === "individuals" && (
          <View className="pb-8">
            {loading ? (
              <View className="py-12 items-center">
                <Text style={{ color: colors.textSecondary }}>Laden...</Text>
              </View>
            ) : individuals.length === 0 ? (
              <View className="py-12 items-center">
                <Ionicons name="person-outline" size={48} color={colors.border} />
                <Text
                  className="mt-4 text-center"
                  style={{ color: colors.textSecondary }}
                >
                  Nog geen individuele scores
                </Text>
                <Text
                  className="mt-2 text-center text-sm px-8"
                  style={{ color: colors.textTertiary }}
                >
                  Individuele scores worden toegekend wanneer een admin punten
                  toekent aan specifieke gebruikers.
                </Text>
              </View>
            ) : (
              individuals.map((individual) => {
                const medalColor = getMedalColor(individual.rank);
                return (
                  <TouchableOpacity
                    key={individual.user_id}
                    className="rounded-xl p-4 mb-3 flex-row items-center"
                    style={{
                      backgroundColor: colors.surface,
                      borderWidth: medalColor ? 2 : 1,
                      borderColor: medalColor || colors.border,
                    }}
                    activeOpacity={0.7}
                    onPress={() =>
                      router.push(`/scores/user/${individual.user_id}`)
                    }
                    accessibilityRole="button"
                    accessibilityLabel={`${individual.full_name || individual.nickname}, ${individual.total_points} punten`}
                  >
                    {/* Rank */}
                    <View
                      className="w-8 h-8 rounded-full items-center justify-center mr-3"
                      style={{
                        backgroundColor: medalColor
                          ? `${medalColor}20`
                          : colors.surfaceSecondary,
                      }}
                    >
                      {medalColor ? (
                        <Ionicons name="trophy" size={16} color={medalColor} />
                      ) : (
                        <Text
                          className="font-bold"
                          style={{ color: colors.textTertiary }}
                        >
                          {individual.rank}
                        </Text>
                      )}
                    </View>

                    {/* Avatar */}
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center mr-3 overflow-hidden"
                      style={{ backgroundColor: colors.primaryLight }}
                    >
                      {individual.avatar_url ? (
                        <Image
                          source={{ uri: individual.avatar_url }}
                          className="w-full h-full"
                          resizeMode="cover"
                        />
                      ) : (
                        <Text
                          className="font-bold"
                          style={{ color: colors.primary }}
                        >
                          {getInitials(
                            individual.nickname || individual.full_name
                          )}
                        </Text>
                      )}
                    </View>

                    {/* User Info */}
                    <View className="flex-1">
                      <Text
                        className="font-semibold"
                        style={{ color: colors.text }}
                      >
                        {individual.nickname || individual.full_name || "Onbekend"}
                      </Text>
                      {individual.team_name && (
                        <View className="flex-row items-center mt-0.5">
                          <View
                            className="w-2 h-2 rounded-full mr-1.5"
                            style={{
                              backgroundColor:
                                individual.team_color || colors.primary,
                            }}
                          />
                          <Text
                            className="text-sm"
                            style={{ color: colors.textSecondary }}
                          >
                            {individual.team_name}
                          </Text>
                        </View>
                      )}
                    </View>

                    {/* Score */}
                    <View className="items-end">
                      <Text
                        className="font-bold text-lg"
                        style={{ color: colors.primary }}
                      >
                        {individual.total_points.toLocaleString()}
                      </Text>
                      <Text
                        className="text-xs"
                        style={{ color: colors.textTertiary }}
                      >
                        punten
                      </Text>
                    </View>

                    <Ionicons
                      name="chevron-forward"
                      size={20}
                      color={colors.textTertiary}
                      style={{ marginLeft: 8 }}
                    />
                  </TouchableOpacity>
                );
              })
            )}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}

